import { card, makeCard, RANK_CHARS } from '../core/cards';
import { comboIndex, COMBO_COUNT } from './combos';
const rankIdx = (ch) => RANK_CHARS.indexOf(ch.toUpperCase());
const pairCombos = (r) => {
    const out = [];
    for (let s1 = 0; s1 < 4; s1++)
        for (let s2 = s1 + 1; s2 < 4; s2++)
            out.push(comboIndex(makeCard(r, s1), makeCard(r, s2)));
    return out;
};
const suitedCombos = (hi, lo) => {
    const out = [];
    for (let s = 0; s < 4; s++)
        out.push(comboIndex(makeCard(hi, s), makeCard(lo, s)));
    return out;
};
const offsuitCombos = (hi, lo) => {
    const out = [];
    for (let s1 = 0; s1 < 4; s1++)
        for (let s2 = 0; s2 < 4; s2++)
            if (s1 !== s2)
                out.push(comboIndex(makeCard(hi, s1), makeCard(lo, s2)));
    return out;
};
const assign = (weights, combos, weight) => {
    for (const idx of combos)
        weights[idx] = weight;
};
const range = (a, b) => {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const out = [];
    for (let v = lo; v <= hi; v++)
        out.push(v);
    return out;
};
/** Split on commas at paren depth 0 (so weighted groups stay intact). */
const splitTopLevel = (src) => {
    const out = [];
    let depth = 0;
    let start = 0;
    for (let i = 0; i < src.length; i++) {
        const ch = src[i];
        if (ch === '(')
            depth++;
        else if (ch === ')')
            depth--;
        else if (ch === ',' && depth === 0) {
            out.push(src.slice(start, i));
            start = i + 1;
        }
    }
    out.push(src.slice(start));
    return out.map((t) => t.trim()).filter((t) => t.length > 0);
};
const PAIR = /^([2-9TJQKA])\1(\+)?$/i;
const PAIR_RANGE = /^([2-9TJQKA])\1-([2-9TJQKA])\2$/i; // e.g. 99-55
const SO = /^([2-9TJQKA])([2-9TJQKA])([so])(\+)?$/i; // AJs, AJs+, KTo+
const SO_RANGE = /^([2-9TJQKA])([2-9TJQKA])([so])-([2-9TJQKA])([2-9TJQKA])([so])$/i; // T9s-76s
const UNSUITED = /^([2-9TJQKA])([2-9TJQKA])(\+)?$/i; // AK, AK+ (both suits)
const SPECIFIC = /^([2-9TJQKA][cdhs])([2-9TJQKA][cdhs])$/i; // exact combo, e.g. AsKs, 7s7d
/** Normalize a card token to canonical case (uppercase rank, lowercase suit) for card(). */
const normCard = (s) => s[0].toUpperCase() + s[1].toLowerCase();
/** Expand a suited-or-offsuit run between two endpoints into (hi,lo) rank pairs. */
const expandSoRange = (hiA, loA, hiB, loB) => {
    if (hiA === hiB)
        return range(loA, loB).map((lo) => [hiA, lo]);
    if (loA === loB)
        return range(hiA, hiB).map((hi) => [hi, loA]);
    if (hiA - loA === hiB - loB) {
        const gap = hiA - loA;
        return range(hiA, hiB).map((hi) => [hi, hi - gap]);
    }
    return [[hiA, loA], [hiB, loB]]; // non-uniform endpoints: include just the two given
};
const expandToken = (tokenRaw, weight, weights) => {
    const token = tokenRaw.replace(/\s+/g, '');
    if (token.length === 0)
        return;
    let m;
    if ((m = SPECIFIC.exec(token))) {
        const c1 = card(normCard(m[1]));
        const c2 = card(normCard(m[2]));
        if (c1 === c2)
            throw new SyntaxError(`combo needs two distinct cards: '${tokenRaw}'`);
        weights[comboIndex(c1, c2)] = weight;
        return;
    }
    if ((m = PAIR.exec(token))) {
        const r = rankIdx(m[1]);
        const ranks = m[2] === '+' ? range(r, 12) : [r];
        for (const rr of ranks)
            assign(weights, pairCombos(rr), weight);
        return;
    }
    if ((m = PAIR_RANGE.exec(token))) {
        for (const rr of range(rankIdx(m[1]), rankIdx(m[2])))
            assign(weights, pairCombos(rr), weight);
        return;
    }
    if ((m = SO.exec(token))) {
        let hi = rankIdx(m[1]);
        let lo = rankIdx(m[2]);
        if (hi < lo)
            [hi, lo] = [lo, hi];
        const suited = m[3].toLowerCase() === 's';
        const combos = suited ? suitedCombos : offsuitCombos;
        if (m[4] === '+') {
            // fix the high card; walk the low card up to just below it (T9s+ ⇒ only T9s).
            for (let l = lo; l < hi; l++)
                assign(weights, combos(hi, l), weight);
        }
        else {
            assign(weights, combos(hi, lo), weight);
        }
        return;
    }
    if ((m = SO_RANGE.exec(token))) {
        const suitedA = m[3].toLowerCase() === 's';
        const suitedB = m[6].toLowerCase() === 's';
        if (suitedA !== suitedB)
            throw new SyntaxError(`mixed suited/offsuit run: '${tokenRaw}'`);
        let hiA = rankIdx(m[1]);
        let loA = rankIdx(m[2]);
        let hiB = rankIdx(m[4]);
        let loB = rankIdx(m[5]);
        if (hiA < loA)
            [hiA, loA] = [loA, hiA];
        if (hiB < loB)
            [hiB, loB] = [loB, hiB];
        const combos = suitedA ? suitedCombos : offsuitCombos;
        for (const [hi, lo] of expandSoRange(hiA, loA, hiB, loB))
            assign(weights, combos(hi, lo), weight);
        return;
    }
    if ((m = UNSUITED.exec(token))) {
        let hi = rankIdx(m[1]);
        let lo = rankIdx(m[2]);
        if (hi < lo)
            [hi, lo] = [lo, hi];
        // no s/o ⇒ every combo of the two ranks (suited + offsuit).
        if (m[3] === '+') {
            for (let l = lo; l < hi; l++) {
                assign(weights, suitedCombos(hi, l), weight);
                assign(weights, offsuitCombos(hi, l), weight);
            }
        }
        else {
            assign(weights, suitedCombos(hi, lo), weight);
            assign(weights, offsuitCombos(hi, lo), weight);
        }
        return;
    }
    throw new SyntaxError(`unrecognized range token: '${tokenRaw}'`);
};
const WEIGHTED_GROUP = /^([0-9]*\.?[0-9]+)\((.*)\)$/;
const parseInto = (src, weight, weights) => {
    for (const token of splitTopLevel(src)) {
        const g = WEIGHTED_GROUP.exec(token);
        if (g) {
            const inner = parseFloat(g[1]);
            if (!(inner >= 0 && inner <= 1))
                throw new SyntaxError(`weight must be in [0,1]: '${token}'`);
            parseInto(g[2], weight * inner, weights); // nested groups multiply
        }
        else {
            expandToken(token, weight, weights);
        }
    }
};
/**
 * Parse an equilab/PokerStove range string into a WeightedRange (no card removal applied).
 * An empty string yields an all-zero range.
 */
export const parseRange = (src) => {
    const weights = new Float32Array(COMBO_COUNT);
    parseInto(src, 1, weights);
    return { weights, removed: new Uint16Array(0) };
};
//# sourceMappingURL=parse.js.map